/**
 * Formatting utilities for large numbers
 * Implements scientific notation and human-readable formats
 */

export interface ScientificNotation {
  mantissa: number;
  exponent: number;
}

/**
 * Convert a number to scientific notation
 */
export function toScientificNotation(value: number): ScientificNotation {
  if (value === 0) {
    return { mantissa: 0, exponent: 0 };
  }

  const exponent = Math.floor(Math.log10(Math.abs(value)));
  const mantissa = value / Math.pow(10, exponent);

  return { mantissa, exponent };
}

/**
 * Format a number for display using scientific notation when appropriate
 * Uses abbreviated notation for large numbers (e.g., 1.5K, 2.3M, 4.5B)
 */
export function formatNumber(value: number, precision: number = 2): string {
  if (!isFinite(value) || isNaN(value)) {
    return '0';
  }

  const absValue = Math.abs(value);
  const sign = value < 0 ? '-' : '';

  // For small numbers, show as-is
  if (absValue < 1000) {
    return sign + absValue.toFixed(Math.max(0, precision - Math.floor(Math.log10(absValue + 1))));
  }

  // Suffixes for abbreviated notation
  const suffixes = [
    { threshold: 1e3, suffix: 'K' },    // Thousand
    { threshold: 1e6, suffix: 'M' },    // Million
    { threshold: 1e9, suffix: 'B' },    // Billion
    { threshold: 1e12, suffix: 'T' },   // Trillion
    { threshold: 1e15, suffix: 'Qa' },  // Quadrillion
    { threshold: 1e18, suffix: 'Qi' },  // Quintillion
    { threshold: 1e21, suffix: 'Sx' },  // Sextillion
    { threshold: 1e24, suffix: 'Sp' },  // Septillion
    { threshold: 1e27, suffix: 'Oc' },  // Octillion
    { threshold: 1e30, suffix: 'No' },  // Nonillion
    { threshold: 1e33, suffix: 'Dc' },  // Decillion
  ];

  // Find appropriate suffix
  for (let i = suffixes.length - 1; i >= 0; i--) {
    if (absValue >= suffixes[i].threshold) {
      const scaled = absValue / suffixes[i].threshold;
      return sign + scaled.toFixed(precision) + suffixes[i].suffix;
    }
  }

  // For extremely large numbers, use scientific notation
  if (absValue >= 1e36) {
    const { mantissa, exponent } = toScientificNotation(value);
    return `${sign}${mantissa.toFixed(precision)}e${exponent}`;
  }

  return sign + absValue.toFixed(0);
}

/**
 * Format a number per second rate
 */
export function formatRate(value: number, precision: number = 2): string {
  return `${formatNumber(value, precision)}/s`;
}

/**
 * Format time in seconds to human-readable format
 */
export function formatTime(seconds: number): string {
  if (seconds < 60) {
    return `${Math.floor(seconds)}s`;
  } else if (seconds < 3600) {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}m ${secs}s`;
  } else {
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    return `${hours}h ${mins}m`;
  }
}

/**
 * Parse a formatted number back to its numeric value
 */
export function parseFormattedNumber(formatted: string): number {
  const suffixMap: { [key: string]: number } = {
    'K': 1e3,
    'M': 1e6,
    'B': 1e9,
    'T': 1e12,
    'Qa': 1e15,
    'Qi': 1e18,
    'Sx': 1e21,
    'Sp': 1e24,
    'Oc': 1e27,
    'No': 1e30,
    'Dc': 1e33,
  };

  // Check for scientific notation
  if (formatted.includes('e')) {
    return parseFloat(formatted);
  }

  // Check for suffix notation
  for (const [suffix, multiplier] of Object.entries(suffixMap)) {
    if (formatted.endsWith(suffix)) {
      const numPart = formatted.slice(0, -suffix.length);
      return parseFloat(numPart) * multiplier;
    }
  }

  return parseFloat(formatted);
}

